import {
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from "react";
import { cn } from "../../lib/cn";

// ─── Types ──────────────────────────────────────────────────

export interface MentionChipData {
  id: string;
  filename: string;
  doc_type?: string;
}

export interface RichChatInputHandle {
  focus: () => void;
  clear: () => void;
  insertChip: (file: MentionChipData) => void;
  getContent: () => { text: string; fileIds: string[] | undefined };
  isEmpty: () => boolean;
  getElement: () => HTMLDivElement | null;
}

interface RichChatInputProps {
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  maxHeight?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onMentionTrigger?: (filter: string | null) => void;
  onChipAdded?: (fileId: string) => void;
  onChipRemoved?: (fileId: string) => void;
  onContentChange?: (hasContent: boolean) => void;
}

// ─── Constants ──────────────────────────────────────────────

const ZERO_WIDTH_SPACE = "\u200B";
const MENTION_ATTR = "data-mention-id";
const MENTION_FILENAME_ATTR = "data-mention-filename";


// ─── Helpers ────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function createChipElement(file: MentionChipData): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.contentEditable = "false";
  chip.setAttribute(MENTION_ATTR, file.id);
  chip.setAttribute(MENTION_FILENAME_ATTR, file.filename || "");
  if (file.doc_type) chip.setAttribute("data-mention-doctype", file.doc_type);
  chip.className = "mention-chip-inline";
  chip.innerHTML = `<span class="mention-chip-icon"></span>${escapeHtml(file.filename)}`;
  return chip;
}

function walkNodes(
  root: HTMLElement,
): { text: string; fileIds: string[] } {
  const parts: string[] = [];
  const fileIds: string[] = [];

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent || "").replace(/\u200B/g, "");
      if (t) parts.push(t);
    } else if (node instanceof HTMLElement) {
      const mentionId = node.getAttribute(MENTION_ATTR);
      if (mentionId) {
        fileIds.push(mentionId);
      } else if (node.tagName === "BR") {
        parts.push("\n");
      } else {
        node.childNodes.forEach(walk);
      }
    }
  }

  root.childNodes.forEach(walk);
  return { text: parts.join("").trim(), fileIds };
}

/**
 * Find the `@...` trigger text range ending at the current cursor position.
 * Returns [textNode, startOffset, endOffset] or null.
 */
function findAtTrigger(
  node: Node,
  offset: number,
): [Text, number, number] | null {
  if (node.nodeType !== Node.TEXT_NODE) return null;
  const text = node.textContent || "";
  const before = text.substring(0, offset);
  const match = before.match(/(^|[\s])@([^\s@]*)$/);
  if (!match) return null;
  const atStart = match.index! + match[1].length;
  return [node as Text, atStart, offset];
}

// ─── Component ──────────────────────────────────────────────

export const RichChatInput = forwardRef<RichChatInputHandle, RichChatInputProps>(
  (
    {
      placeholder,
      disabled,
      className,
      style,
      maxHeight = 120,
      onKeyDown,
      onMentionTrigger,
      onChipAdded,
      onChipRemoved,
      onContentChange,
    },
    ref,
  ) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [hasContent, setHasContent] = useState(false);

    // ─── Content state ───

    const syncContentState = useCallback(() => {
      const el = divRef.current;
      if (!el) return;
      const { text, fileIds } = walkNodes(el);
      const has = text.length > 0 || fileIds.length > 0;
      setHasContent(has);
      onContentChange?.(has);
    }, [onContentChange]);

    // ─── Auto-resize ───

    const autoResize = useCallback(() => {
      const el = divRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    }, [maxHeight]);

    // ─── Imperative API ───

    const focus = useCallback(() => {
      const el = divRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      if (sel && el.childNodes.length > 0) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, []);

    const clear = useCallback(() => {
      if (divRef.current) {
        divRef.current.innerHTML = "";
        setHasContent(false);
        onContentChange?.(false);
        autoResize();
      }
    }, [onContentChange, autoResize]);

    const getContent = useCallback((): {
      text: string;
      fileIds: string[] | undefined;
    } => {
      const el = divRef.current;
      if (!el) return { text: "", fileIds: undefined };
      const { text, fileIds } = walkNodes(el);
      return { text, fileIds: fileIds.length > 0 ? fileIds : undefined };
    }, []);

    const isEmpty = useCallback(() => {
      const { text, fileIds } = getContent();
      return !text && (!fileIds || fileIds.length === 0);
    }, [getContent]);

    const insertChip = useCallback(
      (file: MentionChipData) => {
        const el = divRef.current;
        if (!el) return;

        el.focus();
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const trigger = findAtTrigger(range.startContainer, range.startOffset);

        if (trigger) {
          const [textNode, atStart, atEnd] = trigger;
          const fullText = textNode.textContent || "";
          const beforeAt = fullText.substring(0, atStart);
          const afterCursor = fullText.substring(atEnd);

          const chip = createChipElement(file);
          const zwsp = document.createTextNode(ZERO_WIDTH_SPACE);
          const parent = textNode.parentNode!;

          textNode.textContent = beforeAt;

          const insertBefore = textNode.nextSibling;
          if (insertBefore) {
            parent.insertBefore(chip, insertBefore);
            parent.insertBefore(zwsp, insertBefore);
            if (afterCursor) {
              parent.insertBefore(
                document.createTextNode(afterCursor),
                insertBefore,
              );
            }
          } else {
            parent.appendChild(chip);
            parent.appendChild(zwsp);
            if (afterCursor) {
              parent.appendChild(document.createTextNode(afterCursor));
            }
          }

          // Cursor after zero-width space
          const newRange = document.createRange();
          newRange.setStart(zwsp, 1);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        } else {
          // Fallback: append chip at end
          const chip = createChipElement(file);
          const zwsp = document.createTextNode(ZERO_WIDTH_SPACE);
          el.appendChild(chip);
          el.appendChild(zwsp);
          const newRange = document.createRange();
          newRange.setStart(zwsp, 1);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }

        onChipAdded?.(file.id);
        syncContentState();
        autoResize();
        onMentionTrigger?.(null);
      },
      [onChipAdded, syncContentState, autoResize, onMentionTrigger],
    );

    useImperativeHandle(
      ref,
      () => ({
        focus,
        clear,
        insertChip,
        getContent,
        isEmpty,
        getElement: () => divRef.current,
      }),
      [focus, clear, insertChip, getContent, isEmpty],
    );

    // ─── @ trigger detection ───

    const detectMentionTrigger = useCallback(() => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) {
        onMentionTrigger?.(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const trigger = findAtTrigger(range.startContainer, range.startOffset);
      if (trigger) {
        const [textNode, atStart] = trigger;
        const filter = (textNode.textContent || "").substring(
          atStart + 1,
          range.startOffset,
        );
        onMentionTrigger?.(filter);
      } else {
        onMentionTrigger?.(null);
      }
    }, [onMentionTrigger]);

    // ─── Input handler ───

    const handleInput = useCallback(() => {
      syncContentState();
      autoResize();
      detectMentionTrigger();
    }, [syncContentState, autoResize, detectMentionTrigger]);

    // ─── Chip deletion via backspace ───

    const tryDeleteChipBefore = useCallback(
      (sel: Selection): boolean => {
        if (!sel.isCollapsed || sel.rangeCount === 0) return false;
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const offset = range.startOffset;

        // Case 1: Cursor at start of a text node — check previous sibling
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          let prev: Node | null = node.previousSibling;
          // Skip zero-width space text nodes
          if (
            prev &&
            prev.nodeType === Node.TEXT_NODE &&
            prev.textContent === ZERO_WIDTH_SPACE
          ) {
            const zwsp = prev as ChildNode;
            prev = prev.previousSibling;
            if (prev instanceof HTMLElement && prev.hasAttribute(MENTION_ATTR)) {
              const fileId = prev.getAttribute(MENTION_ATTR)!;
              zwsp.remove();
              prev.remove();
              onChipRemoved?.(fileId);
              syncContentState();
              autoResize();
              return true;
            }
          }
          if (prev instanceof HTMLElement && prev.hasAttribute(MENTION_ATTR)) {
            const fileId = prev.getAttribute(MENTION_ATTR)!;
            prev.remove();
            onChipRemoved?.(fileId);
            syncContentState();
            autoResize();
            return true;
          }
        }

        // Case 2: Cursor in text after ZWS that follows a chip
        if (
          node.nodeType === Node.TEXT_NODE &&
          offset === 1 &&
          (node.textContent || "")[0] === ZERO_WIDTH_SPACE
        ) {
          const prev = node.previousSibling;
          if (prev instanceof HTMLElement && prev.hasAttribute(MENTION_ATTR)) {
            const fileId = prev.getAttribute(MENTION_ATTR)!;
            node.textContent = (node.textContent || "").substring(1);
            prev.remove();
            const newRange = document.createRange();
            newRange.setStart(node, 0);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
            onChipRemoved?.(fileId);
            syncContentState();
            autoResize();
            return true;
          }
        }

        // Case 3: Cursor is directly in the contentEditable (offset points to a child)
        if (node === divRef.current && offset > 0) {
          const prevChild = node.childNodes[offset - 1];
          if (
            prevChild instanceof HTMLElement &&
            prevChild.hasAttribute(MENTION_ATTR)
          ) {
            const fileId = prevChild.getAttribute(MENTION_ATTR)!;
            // Also remove trailing zero-width space
            const next = prevChild.nextSibling;
            if (
              next &&
              next.nodeType === Node.TEXT_NODE &&
              next.textContent === ZERO_WIDTH_SPACE
            ) {
              next.remove();
            }
            prevChild.remove();
            onChipRemoved?.(fileId);
            syncContentState();
            autoResize();
            return true;
          }
        }

        return false;
      },
      [onChipRemoved, syncContentState, autoResize],
    );

    // ─── Key handler ───

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Backspace") {
          const sel = window.getSelection();
          if (sel && tryDeleteChipBefore(sel)) {
            e.preventDefault();
            return;
          }
        }

        // Shift+Enter inserts a line break
        if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            const br = document.createElement("br");
            range.insertNode(br);
            range.setStartAfter(br);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            autoResize();
          }
          return;
        }

        onKeyDown?.(e);
      },
      [onKeyDown, tryDeleteChipBefore, autoResize],
    );

    // ─── Paste handler (plain text only) ───

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        if (!text) return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        syncContentState();
        autoResize();
        detectMentionTrigger();
      },
      [syncContentState, autoResize, detectMentionTrigger],
    );

    // ─── Click handler — re-detect trigger on click ───

    const handleClick = useCallback(() => {
      detectMentionTrigger();
    }, [detectMentionTrigger]);

    // ─── Prevent the browser from creating <div> on Enter ───
    // (handled by parent via onKeyDown preventing Enter)

    // ─── Initial auto-resize on mount ───

    useEffect(() => {
      autoResize();
    }, [autoResize]);

    // ─── Render ───

    return (
      <div className="relative">
        {/* Placeholder shown when empty */}
        {!hasContent && placeholder && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center text-[var(--text-muted)] select-none"
            aria-hidden
          >
            <span className="truncate">{placeholder}</span>
          </div>
        )}
        <div
          ref={divRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-placeholder={placeholder}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={handleClick}
          className={cn(
            "rich-chat-input outline-none whitespace-pre-wrap break-words",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
          style={{
            ...style,
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            minHeight: "1.5em",
          }}
        />
      </div>
    );
  },
);

RichChatInput.displayName = "RichChatInput";

export { MENTION_ATTR };
